import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useWebSocket } from '../hooks/useWebSocket';
import axios from 'axios';
import { Check, X, Clock, Trophy, Frown } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MobileParticipant = () => {
  const { id: rawId } = useParams();
  const [userId, setUserId] = useState(null);
  const [joined, setJoined] = useState(false);
  const [gameState, setGameState] = useState('lobby');
  const [currentQuestion, setCurrentQuestion] = useState(null);
  const [selectedAnswer, setSelectedAnswer] = useState(null);
  const [answerSubmitted, setAnswerSubmitted] = useState(false);
  const [eliminated, setEliminated] = useState(false);
  const [correctAnswer, setCorrectAnswer] = useState(null);
  const [timer, setTimer] = useState(0);
  const [userName, setUserName] = useState('');
  const { lastMessage } = useWebSocket();

  // Generate or use provided user ID
  useEffect(() => {
    if (rawId) {
      setUserId(rawId);
    } else {
      const storedId = localStorage.getItem('game_user_id');
      if (storedId) {
        setUserId(storedId);
      } else {
        const newId = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        localStorage.setItem('game_user_id', newId);
        setUserId(newId);
      }
    }
  }, [rawId]);

  // Handle WebSocket messages
  useEffect(() => {
    if (!lastMessage) return;

    if (lastMessage.type === 'game_state') {
      setGameState(lastMessage.state);
      
      if (lastMessage.state === 'lobby') {
        setCurrentQuestion(null);
        setSelectedAnswer(null);
        setAnswerSubmitted(false);
        setCorrectAnswer(null);
      }
    } else if (lastMessage.type === 'question') {
      setGameState('question');
      setCurrentQuestion(lastMessage.question_data);
      setSelectedAnswer(null);
      setAnswerSubmitted(false);
      setCorrectAnswer(null);
      setTimer(lastMessage.question_data.timer);
    } else if (lastMessage.type === 'results') {
      setCorrectAnswer(lastMessage.correct_answer);
      // Check if user was eliminated
      if (selectedAnswer && selectedAnswer !== lastMessage.correct_answer) {
        setEliminated(true);
      } else if (!selectedAnswer && answerSubmitted === false) {
        setEliminated(true);
      }
    }
  }, [lastMessage, selectedAnswer, answerSubmitted]);

  // Timer countdown
  useEffect(() => {
    if (gameState === 'question' && timer > 0) {
      const interval = setInterval(() => {
        setTimer(t => t > 0 ? t - 1 : 0);
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [gameState, timer]);

  // Check user status when joining
  const checkUserStatus = useCallback(async () => {
    if (!userId || !joined) return;
    try {
      const res = await axios.get(`${API}/game/user/${userId}`);
      if (res.data.eliminated) {
        setEliminated(true);
      }
    } catch (e) {
      console.log('User not found, will join');
    }
  }, [userId, joined]);

  useEffect(() => {
    checkUserStatus();
  }, [checkUserStatus]);

  const handleJoin = async () => {
    if (!userId) return;
    try {
      await axios.post(`${API}/game/join`, {
        user_id: userId,
        name: userName || `Player_${userId.slice(0, 6)}`
      });
      setJoined(true);
    } catch (e) {
      console.error('Failed to join:', e);
    }
  };

  const handleSelectAnswer = async (option) => {
    if (answerSubmitted || eliminated || timer === 0) return;
    
    setSelectedAnswer(option);
    setAnswerSubmitted(true);

    try {
      await axios.post(`${API}/quiz/answer`, {
        user_id: userId,
        question_id: currentQuestion.id,
        selected_option: option
      });
    } catch (e) {
      console.error('Failed to submit answer:', e);
    }
  };

  // Not joined yet - show join screen
  if (!joined) {
    return (
      <div className="mobile-layout" data-testid="mobile-join-screen">
        <div className="mobile-header">
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-2">
            Join the Game
          </h1>
          <p className="text-slate-400">Enter your name to play</p>
        </div>

        <div className="mobile-content justify-center">
          <input
            type="text"
            placeholder="Your Name"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            className="w-full p-4 rounded-xl bg-slate-800 border border-slate-700 text-white text-lg placeholder-slate-500 focus:outline-none focus:border-blue-500"
            data-testid="name-input"
          />
          
          <button
            onClick={handleJoin}
            className="operator-btn w-full text-xl py-5 mt-4"
            data-testid="join-btn"
          >
            Join Game
          </button>
        </div>
      </div>
    );
  }

  // Eliminated state
  if (eliminated) {
    return (
      <div className="mobile-layout" data-testid="mobile-eliminated-screen">
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-full bg-red-500/20 flex items-center justify-center mb-6">
            <Frown size={48} className="text-red-400" />
          </div>
          <h1 className="text-4xl font-bold text-white font-['Outfit'] mb-4">
            Eliminated!
          </h1>
          <p className="text-slate-400 text-lg">
            Better luck next time!
          </p>
        </div>
      </div>
    );
  }

  // Waiting/Lobby state
  if (gameState === 'lobby' || gameState === 'playing') {
    return (
      <div className="mobile-layout" data-testid="mobile-waiting-screen">
        <div className="flex-1 flex flex-col items-center justify-center text-center">
          <div className="w-24 h-24 rounded-full bg-blue-500/20 flex items-center justify-center mb-6 breathing">
            <Clock size={48} className="text-blue-400" />
          </div>
          <h1 className="text-3xl font-bold text-white font-['Outfit'] mb-4">
            {gameState === 'lobby' ? 'Waiting to Start' : 'Get Ready!'}
          </h1>
          <p className="text-slate-400 text-lg">
            {gameState === 'lobby' 
              ? 'The game will begin shortly...' 
              : 'A question is coming soon...'}
          </p>
          <div className="status-badge waiting mt-6">
            <span className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" />
            Still in the game
          </div>
        </div>
      </div>
    );
  }

  // Question state
  if (gameState === 'question' && currentQuestion) {
    return (
      <div className="mobile-layout" data-testid="mobile-question-screen">
        {/* Timer */}
        <div className="text-center mb-6">
          <div className={`inline-flex items-center justify-center w-16 h-16 rounded-full text-2xl font-bold ${
            timer <= 3 ? 'bg-red-500/20 text-red-400' : 'bg-blue-500/20 text-blue-400'
          }`}>
            {timer}
          </div>
        </div>

        {/* Question */}
        <div className="text-center mb-8">
          <h2 className="text-xl font-bold text-white font-['Outfit']">
            {currentQuestion.question}
          </h2>
        </div>

        {/* Options */}
        <div className="mobile-content gap-4">
          {Object.entries(currentQuestion.options).map(([key, value]) => {
            const isSelected = selectedAnswer === key;
            const isCorrect = correctAnswer === key;
            const isWrong = correctAnswer && selectedAnswer === key && !isCorrect;

            return (
              <button
                key={key}
                onClick={() => handleSelectAnswer(key)}
                disabled={answerSubmitted || timer === 0}
                className={`mobile-btn option relative ${
                  isCorrect ? 'correct' : isWrong ? 'wrong' : isSelected ? 'selected' : ''
                }`}
                data-testid={`mobile-option-${key}`}
              >
                <span className="absolute left-4 top-1/2 -translate-y-1/2 w-8 h-8 rounded-full bg-blue-500/30 flex items-center justify-center text-blue-400 font-bold">
                  {key}
                </span>
                <span className="pl-8">{value}</span>
                {isCorrect && <Check className="absolute right-4 text-green-400" size={24} />}
                {isWrong && <X className="absolute right-4 text-red-400" size={24} />}
              </button>
            );
          })}
        </div>

        {/* Answer status */}
        {answerSubmitted && !correctAnswer && (
          <div className="text-center mt-6">
            <div className="status-badge active inline-flex">
              <Check size={16} />
              Answer Submitted
            </div>
          </div>
        )}

        {/* Result */}
        {correctAnswer && (
          <div className="text-center mt-6">
            {selectedAnswer === correctAnswer ? (
              <div className="status-badge active inline-flex text-lg py-2 px-4">
                <Trophy size={20} />
                You're Still In!
              </div>
            ) : (
              <div className="status-badge eliminated inline-flex text-lg py-2 px-4">
                <X size={20} />
                Wrong Answer
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  return null;
};

export default MobileParticipant;
